<template>
	<Stripe
		:apiKey="stripeApiKey"
		:elementsOptions="options.elements"
		v-slot="{ elements, submit, isLoading }"
		@complete="onCreatedPaymentMethod"
	>
		<div class="card">
			<div class="card-form">
				<div class="providers">
					<img src="@/assets/billing/payment-providers.svg" alt="" />
				</div>
				<div class="row">
					<StripeField
						:loading="isLoading"
						type="cardNumber"
						ref="card"
						:label="t('billing.card_form.card_number')"
						:elements="elements"
						:options="options.field"
						@complete="fields.number = $event"
						@focus="onFocusField('CardNumberField')"
					/>
				</div>
				<div class="row row-inline">
					<StripeField
						:loading="isLoading"
						type="cardExpiry"
						:label="t('billing.card_form.expires_on')"
						:options="options.field"
						:elements="elements"
						@complete="fields.expiry = $event"
						@focus="onFocusField('ExpireOnField')"
					/>
					<StripeField
						:loading="isLoading"
						type="cardCvc"
						:label="t('billing.card_form.security_code')"
						:options="options.field"
						:elements="elements"
						@complete="fields.cvc = $event"
						@focus="onFocusField('SecurityCodeField')"
					/>
				</div>
			</div>
		</div>

		<Teleport v-if="isMounted && isActive" to="#billing-popup-action">
			<PButton
				@click="submit($refs.card)"
				title="CardPayButton"
				:path="path"
				:class="{ disabled: isIncomplete, loading: isLoading }"
				cls="btn-primary btn-block btn-compact"
			>
				{{ t('billing.card_form.pay_now') }}
			</PButton>
		</Teleport>
	</Stripe>
</template>

<script setup>
import useLifeCycleState from '@/composition/lifeCycleState'
import PButton from '@/components/Button'
import { StripeField, Stripe } from '@frontend/simple-billing'
import { computed, reactive } from '@vue/reactivity'
import { inject } from '@vue/runtime-core'
import { useI18n } from 'vue-i18n'
import { useStore } from 'vuex'

const props = defineProps({
	path: {
		type: Array,
		default: () => []
	},
	isActive: {
		type: Boolean,
		default: false
	}
})

const store = useStore()

const { t, locale } = useI18n()

const stripeApiKey = store.getters['env/getProperty']('stripeApiKey')
const stripeProductId = store.getters['env/getProperty']('stripeProductId')

const { isMounted } = useLifeCycleState()

const emits = defineEmits(['createPaymentMethod'])

const options = {
	elements: {
		locale: locale.value
	},
	field: {
		style: {
			base: {
				lineHeight: '1.6'
			}
		}
	}
}

const fields = reactive({
	number: false,
	expiry: false,
	cvc: false
})

const amplitude = inject('amplitude')
const onFocusField = (field) => {
	amplitude.logEvent('FieldClick', {
		title: field,
		path: props.path
	})
}

const isIncomplete = computed(() => Object.values(fields).some((field) => !Boolean(field)))

const onCreatedPaymentMethod = (payment_method_id) => {
	emits('createPaymentMethod', { provider: 'stripe', payment_method_id, stripe_product_id: stripeProductId })
}
</script>

<style lang="scss" scoped>
.card {
	background: #ffffff;
	box-shadow: 0px 4px 35px rgba(0, 0, 0, 0.1);
	border-radius: 1rem;
	padding: 1.2rem;
}

.providers {
	display: flex;
	justify-content: flex-end;

	svg {
		display: block;
	}
}

.row {
	margin-top: 0.7rem;

	&-inline {
		display: flex;
		column-gap: 10px;
	}
}
</style>
